import type { SourceKind, SubmitBody, TopUpView } from '@/api/topup';
import { submitAmounts } from '@/views/vitrine/topupCurrencyFirst';

/**
 * Bank and cash top-ups through the concierge (docs/cabinet-redesign/02 §5.1 В-3, owner 31.10): the form card asks
 * exactly what the in-app form asked before 32ad03fc — the amount (the credit to the card while Transfer Hub funds
 * bank/cash, В1, and the currency the client sends in), the bank and its country or the country and city of the cash
 * hand-over, and a comment — and submits the same request. Pure rules; the card only renders what these return.
 */

export type FiatKind = Extract<SourceKind, 'BANK' | 'CASH'>;

/**
 * Whether the client may pick a cash top-up. Off for now (CPO, 03.11: «скрой пока пополнение наличными»): the cash
 * button, the cash row of the card-fee methods and a `?method=CASH` link are hidden or fall back to the choice card.
 * The cash code stays as it is; set this to true to offer cash again. Requests already filed in cash still show.
 */
export const CASH_TOP_UP_OFFERED = false;

/** The method a link asked for, if the client may pick it now; cash only while {@link CASH_TOP_UP_OFFERED}. */
export function offeredFiatKind(method: string | null | undefined): FiatKind | null {
  const m = (method ?? '').toUpperCase();
  if (m === 'BANK') return 'BANK';
  if (m === 'CASH' && CASH_TOP_UP_OFFERED) return 'CASH';
  return null;
}

export interface TopUpChatForm {
  kind: FiatKind;
  /** The plain decimal the client typed («1000.5»), grouping removed. */
  amount: string;
  /** The currency the client sends in (and, off В1, the currency of `amount`). */
  currency: string;
  bankName: string;
  bankCountry: string;
  country: string;
  city: string;
  comment: string;
}

export function emptyTopUpForm(kind: FiatKind): TopUpChatForm {
  return { kind, amount: '', currency: 'USD', bankName: '', bankCountry: '', country: '', city: '', comment: '' };
}

/** Canonical sender type of every bank top-up: the payer is the client themselves (D-2) — sent, never asked. */
export const SENDER_TYPE = 'INDIVIDUAL';

/**
 * The first unmet requirement → its i18n key (the old form's `missing`), or null when the form can be sent.
 * `cashMinimum` — the cash threshold in the amount's currency; null — none applies.
 */
export function topUpFormProblem(f: TopUpChatForm, cashMinimum: number | null = null): string | null {
  const amount = Number(f.amount);
  if (!(amount > 0)) return 'vitrine.topup.needAmount';
  if (f.currency.trim().length < 2) return 'cabinet.topupChat.needCurrency';
  if (f.kind === 'CASH' && cashMinimum != null && amount < cashMinimum) return 'vitrine.topup.belowCashMin';
  if (f.kind === 'BANK') {
    if (f.bankName.trim().length < 2) return 'vitrine.topup.needBank';
    if (f.bankCountry.trim().length < 2) return 'vitrine.topup.needBankCountry';
    return null;
  }
  if (f.country.trim().length < 2 || f.city.trim().length < 2) return 'vitrine.topup.needCountryCity';
  return null;
}

/**
 * The request body. Currency first (docs/TARIFF_RATE_MARKUP.md, owner 03.11.2026): the amount is what the client wants
 * on the account, in `currency`, every commission on top — the core derives the credit to the card from it, with the
 * same quote the form's estimate showed. Never the В1 `creditAmount` in the card currency any more.
 */
export function topUpSubmitBody(f: TopUpChatForm): SubmitBody {
  const bank = f.kind === 'BANK';
  return {
    ...submitAmounts(f.kind, f.currency.trim(), f.amount),
    sourceKind: f.kind,
    bankCountry: bank ? f.bankCountry.trim().toUpperCase() : null,
    bankName: bank ? f.bankName.trim() : null,
    senderType: bank ? SENDER_TYPE : null,
    country: bank ? null : f.country.trim(),
    city: bank ? null : f.city.trim(),
    clientComment: f.comment.trim() || null,
  };
}

/**
 * What the client asked for: the sum on the account in their currency (currency first), else the credit to the card
 * (В1), else the amount to send — null when none is known. A currency-first request carries a derived credit too; the
 * client typed the account sum, so that is what their card and thread title repeat.
 */
export function askedAmount(r: Pick<TopUpView, 'amount' | 'amountCurrency' | 'requestedCreditAmount'
  | 'requestedCreditCurrency' | 'requestedAccountAmount'>): { amount: string; currency: string } | null {
  if (r.requestedAccountAmount != null && r.amountCurrency) {
    return { amount: String(r.requestedAccountAmount), currency: r.amountCurrency };
  }
  if (r.requestedCreditAmount != null && r.requestedCreditCurrency) {
    return { amount: String(r.requestedCreditAmount), currency: r.requestedCreditCurrency };
  }
  return r.amount == null ? null : { amount: String(r.amount), currency: r.amountCurrency };
}

/**
 * What the client pays now: the sum the payment service named with the requisites, else (currency first) what the form
 * quoted to pay in the client's currency, else the priced total, else (an amount declared to send) that amount. Null —
 * not known yet («сумму пришлём вместе с реквизитами»). The same order as the request screen's headline.
 */
export function amountToPay(r: Pick<TopUpView, 'payment' | 'amount' | 'amountCurrency' | 'requestedCreditAmount'
  | 'requestedPayAmount'>): { amount: string; currency: string } | null {
  const p = r.payment;
  if (p?.askAmount != null && p.askCurrency) return { amount: String(p.askAmount), currency: p.askCurrency };
  if (r.requestedPayAmount != null && r.amountCurrency) return { amount: String(r.requestedPayAmount), currency: r.amountCurrency };
  if (p?.total != null && p.fixed) return { amount: String(p.total), currency: p.currency };
  if (r.requestedCreditAmount == null && r.amount != null) return { amount: String(r.amount), currency: r.amountCurrency };
  return null;
}

/** «В т.ч. комиссия 30,00 USD (3%)» — our fee, when the request is priced. */
export function feeOf(r: Pick<TopUpView, 'payment'>): { amount: string; currency: string; percent: string | null } | null {
  const p = r.payment;
  if (!p || p.fee == null) return null;
  return { amount: String(p.fee), currency: p.currency, percent: p.percent == null ? null : String(p.percent) };
}

/** The i18n key of the method in a thread title: «банк» / «наличные». */
export function methodShortKey(kind: string | null | undefined): string {
  return kind === 'CASH' ? 'cabinet.topupChat.method.cash' : 'cabinet.topupChat.method.bank';
}

/** The card title of the form and the request: «Пополнение банковским переводом» / «Пополнение наличными». */
export function topUpTitleKey(kind: string | null | undefined): string {
  return kind === 'CASH' ? 'cabinet.topupChat.titleCash' : 'cabinet.topupChat.titleBank';
}

export interface RequisiteLine {
  /** «IBAN», «Получатель»; null — a line of plain text. */
  label: string | null;
  value: string;
}

/** Markdown emphasis and list markers the manager may have used — the copied value must be the bare value. */
function plain(line: string): string {
  return line
    .replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '')
    .replace(/\*\*|__|`/g, '')
    .trim();
}

/**
 * The manager's requisites text split into lines a client can copy one by one — a mistyped IBAN is a lost payment.
 * «Label: value» becomes a labelled line (label up to 40 characters, a value after it); other non-empty lines stay
 * plain text. A link's colon («https://…») is not a label.
 */
export function requisiteLines(text: string | null | undefined): RequisiteLine[] {
  if (!text) return [];
  const out: RequisiteLine[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = plain(raw);
    if (!line) continue;
    const m = /^([^:]{1,40}):\s+(.+)$/.exec(line);
    if (m && !/^https?$/i.test(m[1].trim())) out.push({ label: m[1].trim(), value: m[2].trim() });
    else out.push({ label: null, value: line });
  }
  return out;
}
